import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { useUser } from "@clerk/react";
import { PageHeader } from "../PageHeader";
import { MetricCard } from "../shared/MetricCard";
import { SectionBoundary } from "../shared/SectionBoundary";
import { CumulativeCostChart } from "../shared/CumulativeCostChart";
import { refusalReason } from "../shared/refusalReason";
import { usePeople } from "../aiUsage/usePeople";
import { useNow } from "../aiUsage/useNow";
import {
  formatCount,
  formatDuration,
  formatUsd,
  formatWhen,
  notRoutedWhy,
  projectLabel,
  sessionStatus,
  sourceLabel,
  spendParts,
  type SessionStatus,
} from "../aiUsage/format";
import { nextSessionChange, sessionFlags, type SessionView } from "../aiUsage/sessions";
import { api as convexApi } from "../../../lib/convexApi";

// convex/observability/lib.ts `CallView`, as merged (PR 1b): one model call,
// metadata only.
interface CallView {
  id: string;
  tool: string;
  toolName: string;
  startedAt: number;
  model: string | null;
  durationMs: number | null;
  ttftMs: number | null;
  httpStatus: number | null;
  status: string;
  stopReason: string | null;
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  costMicroUsd: number | null;
  priced: boolean;
  tools: string[];
  parseIssues: string[];
  gatewayDecision: string | null;
}

const PAGE = 50;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

// A call the host refused before it reached a provider: its gatewayDecision
// is "blocked:<why>" (convex/observability/gateway.ts REFUSED_PREFIX). PR 3b
// stores it without counting its usage, so the session's totals leave it out.
function isRefused(c: CallView): boolean {
  return c.gatewayDecision?.startsWith("blocked:") === true;
}

// What happened to a call, in words: a refusal names its control.
function callOutcome(c: CallView): string {
  if (c.gatewayDecision === "blocked:paused") return "refused (paused)";
  if (c.gatewayDecision === "blocked:stopped") return "refused (stopped)";
  if (isRefused(c)) return "refused by the gateway";
  if (c.gatewayDecision === "error") return "gateway error";
  // An error status from the provider is a failure whatever the host recorded
  // as the call's status: a rate-limited call can be recorded "completed".
  if (c.httpStatus !== null && c.httpStatus >= 400) return `failed (${c.httpStatus})`;
  return c.status;
}

// What Stop does and does not do for this session (spec 6.3; PR 3b). The
// owner's Steerly host refuses every further call a stopped session makes
// through the gateway, and ends a Steerly terminal, or the reply a Steerly chat
// is writing, whose key it holds. It cannot refuse calls that do not go
// through the gateway, and it holds no key for a session started outside
// Steerly. A chat between turns reads "ended", so for a chat the copy says it
// takes no further turns through the gateway. A chat routes each message
// afresh (src/gateway/injection.js gatewayEnvFor): a later turn with the
// gateway off or unhealthy goes straight to the provider, and nothing refuses
// it, so the copy says that too.
function stopCopy(s: SessionView): string {
  const host = "The session owner's Steerly host carries out the stop, once that host is online.";
  const noUndo = "It does not undo what the agent already did";
  const outside = "it does not affect AI tools outside Steerly";
  if (s.sourceKind === "steerly-chat") {
    return s.routed
      ? `Steerly ends any reply in progress and refuses this chat's model calls at the AI gateway from now on, so the chat takes no further turns through the gateway. A turn that goes straight to the provider, as when the gateway is off or unhealthy, is not refused. ${noUndo}, and ${outside}. ${host}`
      : `This chat's model calls do not go through the AI gateway, so Steerly cannot refuse them. Stop ends any reply in progress, but it cannot keep the chat from taking new turns. ${noUndo}. ${host}`;
  }
  if (s.sourceKind === "steerly-terminal") {
    return s.routed
      ? `Steerly refuses this session's model calls at the AI gateway from now on and ends its terminal. ${noUndo}, and ${outside}. ${host}`
      : `This session's model calls do not go through the AI gateway, so Steerly cannot refuse them. Stop ends its terminal. ${noUndo}. ${host}`;
  }
  // Started outside Steerly, and routed (canStopSession): only its calls can
  // be refused.
  return `Steerly refuses this session's model calls at the AI gateway from now on. It was not started in Steerly, so Steerly cannot end it. ${noUndo}. ${host}`;
}

// Whether Stop can do anything for a session in this state. A live or idle
// Steerly terminal or chat is ended by its owner's host, routed or not. A
// chat reports an end after every turn, so between turns it reads "ended":
// only the gateway can keep it from taking another, so it needs to be routed.
// An ended terminal has nothing left to stop. A session started outside
// Steerly can only have its calls refused, so it needs to be routed too.
function canStopSession(s: SessionView, state: SessionStatus): boolean {
  const steerly = s.sourceKind === "steerly-terminal" || s.sourceKind === "steerly-chat";
  if (state === "live" || state === "idle") return steerly || s.routed;
  return state === "ended" && s.sourceKind === "steerly-chat" && s.routed;
}

export function AiSessionPage({ sessionId, onBack }: { sessionId: string; onBack: () => void }) {
  return (
    <>
      <PageHeader
        title="AI session"
        actions={
          <button type="button" className="sr-btn sr-btn--ghost" onClick={onBack}>
            Back to sessions
          </button>
        }
      />
      <div className="sr-page-body">
        {/* Keyed by session: another session opens with no stop in progress,
            and one that failed to load is tried again. */}
        <SectionBoundary key={sessionId} label="This session">
          <SessionBody sessionId={sessionId} />
        </SectionBoundary>
      </div>
    </>
  );
}

function SessionBody({ sessionId }: { sessionId: string }) {
  const s = useQuery((convexApi as any).observability.queries.getSession, { sessionId }) as
    | SessionView
    | undefined;
  const stopSession = useMutation((convexApi as any).observability.controls.stopSession);
  const { nameFor } = usePeople();
  const { user } = useUser();
  const me = user?.id ?? null;
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // How many of the loaded calls were refused (CallsSection says so).
  const [refusedLoaded, setRefusedLoaded] = useState(0);
  // Focus follows the confirm panel: to Cancel when it opens, back to Stop
  // when it is cancelled, and to the session's status once the stop is sent,
  // so a keyboard user never lands on the page body.
  const stopRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const statusRef = useRef<HTMLSpanElement>(null);
  const focusNext = useRef<"cancel" | "stop" | "status" | null>(null);
  useEffect(() => {
    const target = focusNext.current;
    if (target === null) return;
    focusNext.current = null;
    const el = target === "cancel" ? cancelRef.current : target === "stop" ? stopRef.current : null;
    (el ?? statusRef.current)?.focus();
  });

  // The time the status and the times are read against, read again when the
  // session would turn idle on its own and at local midnight (useNow).
  const now = useNow((n) => nextSessionChange(s ? [s] : [], n));
  // Whether Stop can do anything for the session as it is now.
  const canStop = s !== undefined && canStopSession(s, sessionStatus(s, now));
  // A confirmation for a stop that no longer applies (the session was
  // stopped, or an unrouted chat's turn ended under it) is dropped for good,
  // not kept to come back on its own when the next turn starts; focus goes to
  // the session's status. It is hidden meanwhile (canStop below). As the
  // redesign's session page does.
  const stale = confirming && !canStop;
  useEffect(() => {
    if (stale) {
      focusNext.current = "status";
      setConfirming(false);
    }
  }, [stale]);

  if (s === undefined) return <div className="sr-empty">Loading session…</div>;

  const state = sessionStatus(s, now);
  // A refusal belongs to the confirmation it answered, and shows only while
  // that confirmation does: one dropped under it (the session was stopped, or
  // an unrouted chat's turn ended, even while the stop was on its way) takes
  // its refusal with it. No confirmation opens while a stop is on its way
  // (Stop reads "Stopping…" and is disabled), so a late refusal never lands
  // under a newer one, and opening one clears the last refusal.
  const shownError = canStop && confirming ? error : null;
  const cost = spendParts(s.costMicroUsd, s.calls, s.unpricedCalls);
  const flags = sessionFlags(s);
  // A session whose only calls were refused counts none: its cost is not
  // "no calls seen" when the timeline lists the refused ones.
  const onlyRefused = s.calls <= 0 && refusedLoaded > 0;
  const costValue = onlyRefused ? "no calls counted" : cost.amount;
  let costNote: string | undefined;
  if (onlyRefused) {
    costNote = `${refusedLoaded} refused`;
  } else if (cost.priced) {
    costNote =
      cost.unpriced === null ? "at API list prices" : `at API list prices, ${cost.unpriced}`;
  }
  const callsNote =
    [
      s.errorCalls ? plural(s.errorCalls, "error", "errors") : null,
      refusedLoaded > 0 ? "refused calls not counted" : null,
    ]
      .filter(Boolean)
      .join(", ") || undefined;
  // By id: a member's display name can be any word, "You" included.
  const stopper = s.stoppedBy ? (s.stoppedBy === me ? "you" : nameFor(s.stoppedBy)) : null;

  async function stop(id: string) {
    setBusy(true);
    setError(null);
    try {
      await stopSession({ sessionId: id });
      focusNext.current = "status";
      setConfirming(false);
    } catch (e) {
      // The server's reason when it gave one; else a sentence, never the
      // client's call trace (as the AI gateway page's controls say it).
      const reason = refusalReason(e, "");
      setError(reason ? `Could not stop the session: ${reason}` : "Could not stop the session.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="sr-ai-head">
        <div className="sr-ai-facts">
          <span>
            <strong>{nameFor(s.userId)}</strong>
          </span>
          <span>
            <strong>
              {s.toolName}
              {s.toolVersion ? ` ${s.toolVersion}` : ""}
            </strong>
          </span>
          {projectLabel(s) && <span>{projectLabel(s)}</span>}
          <span>{sourceLabel(s.sourceKind)}</span>
          {s.steerlyRef && <span>Steerly ref {s.steerlyRef}</span>}
          <span>
            {s.routed
              ? "routed through the AI gateway"
              : notRoutedWhy("not routed", s.notRoutedReason)}
          </span>
          <span>started {formatWhen(s.firstSeenAt, now)}</span>
          <span
            ref={statusRef}
            tabIndex={-1}
            className={`sr-pill${state === "live" ? " is-ok" : state === "stopped" ? " is-crit" : ""}`}
          >
            {state}
          </span>
        </div>
        <div>
          {state === "stopped" && s.stoppedAt && (
            <span className="sr-ai-note">
              Stopped{stopper ? ` by ${stopper}` : ""} at {formatWhen(s.stoppedAt, now)}
            </span>
          )}
          {canStop && !confirming && (
            <button
              ref={stopRef}
              type="button"
              className="sr-btn"
              disabled={busy}
              onClick={() => {
                focusNext.current = "cancel";
                setError(null);
                setConfirming(true);
              }}
            >
              {busy ? "Stopping…" : "Stop session"}
            </button>
          )}
        </div>
      </div>
      {canStop && confirming && (
        <div className="sr-ai-flag is-deny" role="group" aria-label="Confirm stop">
          <p style={{ margin: "0 0 8px" }}>{stopCopy(s)}</p>
          <span className="sr-row-confirm">
            <button
              ref={cancelRef}
              type="button"
              className="sr-btn sr-btn--ghost"
              onClick={() => {
                focusNext.current = "stop";
                setConfirming(false);
                setError(null);
              }}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sr-btn"
              onClick={() => void stop(s.id)}
              disabled={busy}
            >
              {busy ? "Stopping…" : "Confirm stop"}
            </button>
          </span>
        </div>
      )}
      {shownError && (
        <div className="sr-ai-error" role="alert">
          {shownError}
        </div>
      )}

      <div className="sr-metric-grid" style={{ marginTop: 16 }}>
        <MetricCard label="Model calls" value={formatCount(s.calls)} delta={callsNote} />
        <MetricCard
          label="Tokens"
          value={formatCount(
            s.inputTokens + s.outputTokens + s.cacheReadTokens + s.cacheWriteTokens
          )}
          delta={`${formatCount(s.inputTokens)} in, ${formatCount(s.outputTokens)} out, ${formatCount(s.cacheReadTokens)} cache read, ${formatCount(s.cacheWriteTokens)} cache write`}
        />
        <MetricCard label="Cost" value={costValue} delta={costNote} />
        <MetricCard
          label="Duration"
          value={formatDuration((s.endedAt ?? s.lastSeenAt) - s.firstSeenAt)}
        />
        {/* The reasons the list's Flags column gives (sessionFlags). */}
        <MetricCard
          label="Flags"
          value={formatCount(flags.length)}
          delta={flags.length > 0 ? flags.join(", ") : undefined}
          tone={flags.length > 0 ? "ask" : "neutral"}
        />
      </div>

      {/* Its own boundary (spec 9): calls that fail to load leave the header,
          the figures and Stop in place. Labelled so both of its messages read
          right: "The call list could not load", "The call list was not found". */}
      <SectionBoundary label="The call list">
        <CallsSection
          sessionId={sessionId}
          session={s}
          now={now}
          onRefusedLoaded={setRefusedLoaded}
        />
      </SectionBoundary>
    </>
  );
}

function CallsSection({
  sessionId,
  session: s,
  now,
  onRefusedLoaded,
}: {
  sessionId: string;
  session: SessionView;
  /** The page's clock (useNow), for the calls' times. */
  now: number;
  onRefusedLoaded: (refused: number) => void;
}) {
  const { results, status, loadMore } = usePaginatedQuery(
    (convexApi as any).observability.queries.listCalls,
    { sessionId },
    { initialNumItems: PAGE }
  );
  const calls = results as CallView[];
  const refused = calls.filter(isRefused).length;
  useEffect(() => onRefusedLoaded(refused), [refused, onRefusedLoaded]);

  // The tool names the model asked for, counted over the loaded calls. A Map,
  // since a tool name is free text and may be "constructor".
  const toolsUsed = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of calls) for (const t of c.tools) counts.set(t, (counts.get(t) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1));
  }, [calls]);

  if (status === "LoadingFirstPage") {
    return (
      <>
        <h2 className="sr-section-title">Model calls</h2>
        <div className="sr-empty">Loading calls…</div>
      </>
    );
  }

  // Only the latest calls are loaded while more remain.
  const partial = status === "CanLoadMore" || status === "LoadingMore";
  // Every call is loaded, and fewer than the session counts: the retention
  // sweep deleted the older ones, while the session's totals keep them.
  const kept = calls.length - refused;
  const trimmed = status === "Exhausted" && kept < s.calls;

  return (
    <>
      {trimmed && calls.length > 0 && (
        <div className="sr-ai-note">
          Calls older than the org's call retention are deleted: {kept} of this session's{" "}
          {plural(s.calls, "call", "calls")} {kept === 1 ? "is" : "are"} kept. The chart and the
          tool counts cover the kept calls only.
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <CumulativeCostChart calls={calls} partial={partial} />
      </div>

      {toolsUsed.length > 0 && (
        <>
          <h2 className="sr-section-title">
            {partial
              ? "Tools the model asked for, in the latest calls"
              : "Tools the model asked for"}
          </h2>
          <div className="sr-ai-facts">
            {toolsUsed.map(([name, n]) => (
              <span key={name}>
                <strong>{name}</strong> {n}
              </span>
            ))}
          </div>
        </>
      )}

      <h2 className="sr-section-title">Model calls</h2>
      {calls.length === 0 ? (
        <div className="sr-empty">
          {s.calls > 0
            ? "This session's model calls are older than the org's call retention and have been deleted. The figures above still count them."
            : s.routed
              ? "No model calls recorded yet."
              : "This session did not go through the AI gateway, so Steerly did not see its model calls."}
        </div>
      ) : (
        <table className="sr-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Model</th>
              <th className="is-num">In</th>
              <th className="is-num">Out</th>
              <th className="is-num">Cached</th>
              <th className="is-num">Cost</th>
              <th>Tools</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((c) => (
              <tr key={c.id}>
                <td>{formatWhen(c.startedAt, now)}</td>
                <td>{c.model ?? ""}</td>
                <td className="is-num">{formatCount(c.input)}</td>
                <td className="is-num">{formatCount(c.output)}</td>
                <td className="is-num">{formatCount(c.cacheRead)}</td>
                <td className="is-num">
                  {/* A refusal counts in nothing and has no cost to price. */}
                  {isRefused(c) ? "" : c.priced ? formatUsd(c.costMicroUsd) : "not priced"}
                </td>
                <td>{c.tools.join(", ")}</td>
                <td>{callOutcome(c)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {status === "CanLoadMore" && (
        <button
          type="button"
          className="sr-btn sr-btn--ghost"
          style={{ marginTop: 12 }}
          onClick={() => loadMore(PAGE)}
        >
          Load more
        </button>
      )}
    </>
  );
}
